/**
 * Mapeo SPA <-> modulo `finance` del backend.
 *
 * El backend ya devuelve el P&L agrupado y con subtotales: aca NO se recalcula
 * nada. Un margen calculado en dos sitios es un margen que algun dia va a dar
 * dos numeros distintos, y el que se vea primero sera el que alguien use para
 * decidir.
 *
 * Lo unico que pasa aca es forma: `Record<string, number>` de meses -> array
 * ordenado de 12 posiciones, que es lo que una tabla necesita para pintarse sin
 * preocuparse de si el backend omitio un mes vacio.
 */

/**
 * DOS ORIGENES, LA MISMA FORMA.
 *
 * `HOJA_SIETE_FINANZAS` es el modelo de gestion: lo que la empresa usa para
 * decidir, con forecast. `LIBRO` es el mismo estado de resultados derivado de
 * los asientos del libro gerencial. No compiten y no tienen por que coincidir;
 * la diferencia entre ambos es informacion.
 *
 * El backend devuelve la MISMA estructura por los dos caminos a proposito, para
 * que la pantalla que los compara no tenga dos lectores: el dia que uno cambiara
 * empezaria a comparar dos cosas distintas sin avisar.
 */
export type OrigenPnl = "HOJA_SIETE_FINANZAS" | "LIBRO"

export const ORIGENES: Array<{ id: OrigenPnl; label: string; ayuda: string }> = [
  { id: "HOJA_SIETE_FINANZAS", label: "Modelo de gestion",
    ayuda: "La hoja de finanzas. Lleva forecast y criterios de gestion." },
  { id: "LIBRO", label: "Libro contable",
    ayuda: "Derivado de los asientos del libro gerencial. Solo lo asentado." },
]

/** `GET /finance/pnl` */
/** De dónde salen los meses POSTERIORES al corte. */
export type FuentePnl = "hoja" | "declarado"

export interface PnlRow {
  anio: number
  pais: string
  origen: string
  /** La fuente de los meses proyectados. Viaja en la respuesta a propósito. */
  fuente?: FuentePnl
  /**
   * Los meses que NO salen de la hoja.
   *
   * Un P&L construido desde lo declarado tiene las mismas filas y los mismos
   * totales cuadrados que uno de la hoja: sin esta lista, la pantalla no puede
   * señalar qué columnas cambiaron de fuente.
   */
  meses_declarados?: number[]
  moneda: string
  cuentas: Array<{
    account: string
    total: number
    meses: Record<string, number>
    lineas: Array<{ account: string; sub_account: string; total: number; meses: Record<string, number> }>
  }>
  subtotales: {
    margen_bruto: { meses: Record<string, number>; total: number }
    ebitda: { meses: Record<string, number>; total: number }
  }
  indicadores: {
    ingresos: number
    margen_bruto_pct: number | null
    ebitda_pct: number | null
  }
  /* --- Solo con `origen = LIBRO`. Existen para que nada desaparezca en
     silencio: un P&L del libro que descarta la depreciacion y los impuestos se
     ve MEJOR que la realidad, que es la peor forma de equivocarse. --- */
  /** D&A, resultado financiero e impuestos: no tienen linea en el modelo. */
  fuera_del_modelo?: Array<{
    seccion: string
    total: number
    meses: Record<string, number>
  }>
  /** Asientos que no se pudieron convertir a USD. No se cuentan como cero. */
  monedas_sin_tipo_de_cambio?: Record<string, number>
  /** Meses resueltos con el cambio de otro mes, porque la tabla no llega. */
  tipo_de_cambio_arrastrado?: Array<{ moneda: string; mes: number; tomado_de: string }>
  /**
   * Hasta donde llega lo REAL. Lo calcula el backend (`corte_real`): el ultimo
   * mes del ano con asientos en el libro GERENCIAL, y en el consolidado el de
   * la sociedad mas atrasada.
   *
   * `mes: null` NO es "todo es forecast desde enero": es que el libro no tiene
   * nada de ese ano y no hay con que comparar. La pantalla lo dice con palabras
   * en vez de pintar la linea pegada al borde izquierdo.
   */
  corte?: CorteReal
  /**
   * EL P&L ARMADO. Sólo existe si hay una frontera declarada que cae dentro del
   * año: los meses de después salen del libro y del maestro, no de la hoja.
   * `null` = la tabla es la hoja tal cual. Ver `finance/ensamblador.py`.
   */
  ensamblado?: Ensamblado | null
}

/** De dónde salió una celda armada. `mixto` = en el consolidado, cada país de una. */
export type FuenteCelda = "libro" | "maestro" | "mixto"

export interface Ensamblado {
  meses_ensamblados: number[]
  meses_por_pais: Record<string, {
    frontera: Frontera | null
    hoja: number[]
    ensamblado: number[]
  }>
  /** Una por (línea, mes) armado: lo que dijo cada fuente y cuál se usó. */
  celdas: Array<{
    account: string
    sub_account: string
    mes: number
    codigo: string
    libro: number
    maestro: number
    valor: number
    usado: FuenteCelda
  }>
  /** Libro y maestro con dato y distintos: manda uno, y se avisa. */
  descuadres: Array<{
    pais: string
    codigo: string
    cuenta: string | null
    mes: number
    libro: number
    maestro: number
    diferencia: number
    manda: "libro" | "maestro"
    usado: "libro" | "maestro"
  }>
  /** Qué dicen la hoja y lo armado del último mes de la hoja. */
  en_la_frontera: {
    meses: number[]
    cuentas: Array<{
      account: string
      hoja: number
      armado: number
      diferencia: number
      cubre_pct: number | null
    }>
    ebitda: { hoja: number; armado: number }
  } | null
  /** Fila del P&L → líneas de la hoja que junta. No se editan: no se puede repartir. */
  filas_del_puente: Record<string, string[]>
  avisos: string[]
  fuera_de_finanzas: Array<{ codigo: string; cuenta: string | null; importe: number }>
  fuera_del_modelo: Array<{ codigo: string; cuenta: string | null; importe: number }>
  sin_puente: Array<{ account: string; sub_account: string; total: number }>
  maestro_sin_cuenta: Array<{ codigo: string; importe: number }>
  preliminar_por_mes: Record<string, number>
  supuestos_fx: string | null
}

/* ------------------------------------------- qué va a Finanzas (ruteo) --- */

export interface Frontera {
  pais: string | null
  anio: number
  mes: number
  motivo: string
  declarado_en?: string
  alcance?: string
}

export type FuenteFinanzas = "libro" | "maestro"

export interface CuentaRuteo {
  codigo: string
  nombre: string
  nivel: number
  tipo_estado: string
  va_a_finanzas: boolean
  va_a_contabilidad: boolean
  /** La que se declaró. `null` = la deduce el primer dígito. */
  linea_del_modelo: string | null
  /** La que de verdad se usa: la declarada o la del dígito. */
  linea_efectiva: string | null
  fuente_finanzas: FuenteFinanzas
}

export interface RuteoRow {
  cuentas: { total: number; en_finanzas: number; en_contabilidad: number; en_ninguna: number }
  excepciones: number
  frontera: Frontera | null
  nota: string
  detalle: CuentaRuteo[]
  lineas_del_modelo: string[]
}

export interface LineaDelPuente {
  account: string
  sub_account: string
  importe: number
  codigo_grupo: string | null
  cuenta: string | null
  cambia_de_linea: boolean
  linea_destino: string | null
}

export interface PuenteRow {
  anio: number
  lineas: LineaDelPuente[]
  sin_puente: number
  cuentas: Array<{ codigo: string; nombre: string; linea: string }>
}

export interface ContrasteRow {
  anio: number
  pais: string | null
  comparables: Array<{
    codigo: string; cuenta: string; mes: number
    hoja: number; libro: number; diferencia: number
    solo_en: "hoja" | "libro" | null
  }>
  descuadres: ContrasteRow["comparables"]
  sin_puente: Array<{ sub_account: string; importe: number }>
  /** `null` = sin puente no se puede afirmar ni que cuadra ni que no. */
  cuadra: boolean | null
  nota: string
}

export interface CorteReal {
  anio: number
  mes: number | null
  /** Ultimo mes con asientos de cada pais. Es lo que sostiene `regla`. */
  paises: Record<string, number>
  /** Por que la linea va donde va. Se muestra: una linea sin regla no se puede comprobar. */
  regla: string
}

/* -------------------------------------------------- hoja contra libro --- */

export interface FilaComparada {
  account: string
  hoja: number
  libro: number
  /** libro - hoja. */
  diferencia: number
}

/**
 * Las mismas cuentas por los dos caminos, en el orden del P&L.
 *
 * SE PARTE DE LA UNION, no de la hoja. Una cuenta que solo existe en el libro
 * —un gasto asentado que el modelo todavia no contempla— es exactamente lo que
 * esta pantalla tiene que sacar a la luz; recorrer solo las cuentas de la hoja
 * la haria invisible, y la fila desaparecida se leeria como "no hay nada ahi".
 */
export function compararOrigenes(
  hoja: PnlRow | undefined, libro: PnlRow | undefined,
): FilaComparada[] {
  const deHoja = new Map((hoja?.cuentas ?? []).map((c) => [c.account, c.total]))
  const deLibro = new Map((libro?.cuentas ?? []).map((c) => [c.account, c.total]))
  const cuentas = [...new Set([...deHoja.keys(), ...deLibro.keys()])]

  const orden = (a: string) => {
    const i = ORDEN_CUENTAS.indexOf(a)
    return i === -1 ? ORDEN_CUENTAS.length : i
  }

  return cuentas
    .sort((a, b) => orden(a) - orden(b) || a.localeCompare(b))
    .map((account) => {
      const h = deHoja.get(account) ?? 0
      const l = deLibro.get(account) ?? 0
      return { account, hoja: h, libro: l, diferencia: l - h }
    })
}

/**
 * El orden en que se lee un P&L. Espejo de `ORDEN_CUENTAS` del backend: no es
 * alfabetico, es el orden en que las cifras se restan unas de otras.
 */
export const ORDEN_CUENTAS = [
  "Revenue",
  "Cost of Goods Sold (COGS)",
  "Labor Costs",
  "G&A Expenses",
  "Taxes",
  "Profit Sharing",
  "Financing",
  "Investments",
]

/** `GET /finance/pnl/by-country` */
export interface CountryRow {
  pais: string
  ingresos: number
  costo_ventas: number
  costo_equipo: number
  gastos_generales: number
  margen_bruto: number
  ebitda: number
  ebitda_pct: number | null
  participacion_ingresos: number | null
}

/** `GET /finance/cashflow` */
export interface CashflowRow {
  anio: number
  pais: string
  moneda: string
  entradas: Record<string, number>
  salidas: Record<string, number>
  neto: Record<string, number>
  acumulado: Record<string, number>
  /** Lo que el modelo acumulo en los anos ANTERIORES. Antes arrancaba en cero. */
  saldo_inicial: number
  /** Desde que ano hay datos. Antes de el no hay cero: hay ausencia. */
  acumula_desde: number | null
  /** `false` cuando el ano pedido es el primero con datos y no arrastra nada. */
  saldo_inicial_completo: boolean
  /** Saldo real de las cuentas bancarias. NO es lo mismo que el acumulado. */
  caja_bancos: number | null
  cuentas: Array<{ account: string; total: number; meses: Record<string, number> }>
  /** Hasta donde llega lo real. Misma regla que el P&L: ver `CorteReal`. */
  corte?: CorteReal

}

/** `GET /finance/caja-bancos`: bancos hasta el último mes con movimientos, forecast después. */
export interface CajaBancosRow {
  anio: number
  pais: string
  moneda: string
  ultimo_mes_bancario: number | null
  fuente_por_mes: Record<string, "bancos" | "forecast">
  entradas: Record<string, number>
  salidas: Record<string, number>
  neto: Record<string, number>
  acumulado: Record<string, number>
  categorias: Array<{
    categoria: string; nombre: string; total: number; meses: Record<string, number>
    /** Meses futuros cuya celda está pisada a mano. */
    pisados: number[]
  }>
  /** Las celdas futuras pisadas, con su motivo. */
  pisadas: Array<{ mes: number; categoria: string; valor_usd: number; revierte_a: number | null; motivo: string | null; por: string | null }>
  movimientos: number
  /** Movimientos apartados de la caja en Maestros › Movimientos bancarios. */
  no_considerados: number
  sin_cotizacion: number
  saldo_inicial: number
  saldo_inicial_declarado: boolean
  caja_bancos_hoy: number | null
  escenario: string
  nota: string
}

/** `GET /finance/kpis` */
export interface KpisRow {
  anio: number
  /** El alcance que se consultó. `null` es el consolidado, no «no se sabe». */
  pais?: string | null
  moneda: string
  actual: Anio
  anio_anterior: Anio
  variacion: Record<string, number | null>
}

interface Anio {
  ingresos: number
  gasto_operativo: number
  costo_equipo: number
  ebitda: number
  ebitda_pct: number | null
  ingresos_por_linea: Record<string, number>
}

/* ========================================================================== */

/**
 * Meses a array de 12. El backend puede omitir un mes sin dato; la tabla no
 * puede tener huecos, porque una columna que se corre desalinea el año entero.
 */
export function serieMensual(meses: Record<string, number> | undefined): number[] {
  const out: number[] = []
  for (let m = 1; m <= 12; m++) out.push(Number(meses?.[String(m)] ?? 0))
  return out
}

/** Nombre legible del pais. El backend manda el codigo ISO. */
const PAISES: Record<string, string> = {
  PE: "Perú",
  MX: "México",
  BR: "Brasil",
  PA: "Panamá",
  CONSOLIDADO: "Consolidado",
}

export function nombrePais(codigo: string): string {
  return PAISES[codigo] ?? codigo
}

/**
 * Etiqueta en español de cada cuenta del modelo.
 *
 * La hoja esta en ingles porque la hizo finanzas; la aplicacion esta en
 * español. Se traduce SOLO para mostrar: el codigo que viaja y se guarda sigue
 * siendo el de la hoja, para que un numero se pueda rastrear hasta su celda.
 */
const CUENTAS: Record<string, string> = {
  "Revenue": "Ventas",
  "Cost of Goods Sold (COGS)": "Costo de ventas",
  "Labor Costs": "Costo de equipo",
  "G&A Expenses": "Gastos generales",
  "Taxes": "Impuestos",
  "Profit Sharing": "Reparto de utilidades",
  "Financing": "Financiamiento",
  "Investments": "Inversiones",
}

export function nombreCuenta(codigo: string): string {
  return CUENTAS[codigo] ?? codigo
}

/** Las cuentas que RESTAN en el P&L. Define el color y el signo en pantalla. */
export function esCosto(account: string): boolean {
  return account !== "Revenue"
}

/** `GET /finance/ratios` */
export interface RatiosRow {
  periodo: { anio: number; mes_desde: number; mes_hasta: number; meses: number }
  pais: string
  moneda: string
  base: Record<string, number>
  comparacion: {
    /** `interanual` = mismo tramo del año anterior · `anterior` = ventana previa */
    tipo: string
    etiqueta: string
    ventana: { desde: { anio: number; mes: number }; hasta: { anio: number; mes: number } }
  }
  ratios: Ratio[]
  no_disponibles: Array<{ id: string; nombre: string; motivo: string }>
}

export interface Ratio {
  id: string
  nombre: string
  valor: number | null
  /** `pct` fraccion · `veces` multiplo · `moneda` importe en USD */
  formato: "pct" | "veces" | "moneda"
  numerador: number
  denominador: number
  /**
   * La operacion ESCRITA, para los ratios que no son un cociente.
   *
   * La tarjeta pinta «numerador / denominador» debajo del valor —existe para
   * que un porcentaje suelto se pueda comprobar sin salir de la pantalla— y eso
   * solo es verdad cuando el ratio es una division. La Regla del 40 es una SUMA
   * de dos porcentajes y llegaba con `numerador === denominador`: la tarjeta
   * mostraba «465.436,00 / 465.436,00» debajo de un −9,0%, que no comprueba
   * nada e invita a leer 100%.
   *
   * Es la EXCEPCION. Si todos los ratios la trajeran, la tarjeta dejaria de
   * mostrar los dos numeros —que es lo que se audita— para mostrar una frase.
   */
  base?: string | null
  interpretacion: string
  alerta: boolean
}

/** Cómo se compara el período elegido. Son preguntas distintas, no dos formatos. */
export type Comparacion = "interanual" | "anterior"

export const COMPARACIONES: Array<{ id: Comparacion; label: string; ayuda: string }> = [
  {
    id: "interanual",
    label: "Año contra año",
    ayuda: "Mismo tramo del año anterior: feb-26 contra feb-25, Q3 contra Q3. "
      + "Quita la estacionalidad, que con contratos anuales la hay.",
  },
  {
    id: "anterior",
    label: "Período anterior",
    ayuda: "La ventana previa del mismo tamaño: Q3 contra Q2, febrero contra enero. "
      + "Mide impulso y avisa antes; cruza el cambio de año sin problema.",
  },
]

/* ==========================================================================
 * Edicion
 * ==========================================================================
 * FINANZAS Y CONTABILIDAD NO TIENEN POR QUE COINCIDIR. Hay hechos que viven en
 * el libro y no en la gestion —un ajuste tributario que no es un hecho economico
 * del negocio— y al reves: una provision de gestion, un costo imputado a la
 * linea que lo consume aunque contablemente este en otra.
 *
 * Por eso una edicion pide DOS cosas: el motivo, como en toda la aplicacion, y
 * la NATURALEZA de la diferencia. Tratar toda edicion como "corregir un error"
 * convertiria cada divergencia legitima en un incidente.
 */
export type ClasificacionEdicion =
  | "correccion"
  | "solo_finanzas"
  | "solo_contabilidad"
  | "reclasificacion"

export const CLASIFICACIONES: Array<{
  id: ClasificacionEdicion
  label: string
  ayuda: string
}> = [
  { id: "correccion", label: "Corrección",
    ayuda: "La hoja tenía mal el número." },
  { id: "solo_finanzas", label: "Solo finanzas",
    ayuda: "Existe en el modelo de gestión y no en contabilidad." },
  { id: "solo_contabilidad", label: "Solo contabilidad",
    ayuda: "Está en el libro y no corresponde al modelo de gestión." },
  { id: "reclasificacion", label: "Reclasificación",
    ayuda: "El importe es correcto pero pertenece a otra línea." },
]

export interface EdicionCelda {
  pais: string
  bloque: string
  account: string
  sub_account: string
  anio: number
  mes: number
  importe_usd: number
  motivo: string
  clasificacion: ClasificacionEdicion
}

/** `GET /finance/edits` */
export interface EdicionRow {
  pais: string
  bloque: string
  account: string
  sub_account: string
  anio: number
  mes: number
  importe_usd: number
  de_la_hoja: number | null
  delta: number | null
  clasificacion: ClasificacionEdicion
  explicacion: string
  motivo: string
  editado_en: string
}

/* ==========================================================================
 * Conciliacion: finanzas contra facturacion
 * ==========================================================================
 * UNA DIFERENCIA NO ES UN ERROR, y esa es toda la pantalla. Hay ingreso
 * devengado sin factura emitida y facturas que no son ingreso del periodo. Lo
 * que importa es distinguir un DESCALCE REAL —que se investiga— de un REGISTRO
 * INCOMPLETO —que se importa—, porque piden acciones opuestas y un total
 * consolidado las mezcla hasta volverlas indistinguibles.
 */

/** `GET /finance/reconciliation` */
export interface ConciliacionRow {
  anio: number
  moneda: string
  nota: string
  paises: ConciliacionPais[]
}

export type EstadoConciliacion =
  | "cuadra"
  | "diferencia"
  | "registro_incompleto"
  | "sin_ingreso"

export interface ConciliacionPais {
  pais: string
  ingreso: number
  facturado: number
  diferencia: number
  /** Facturado / ingreso. `null` cuando no hay ingreso con que comparar. */
  cobertura: number | null
  facturas: number
  estado: EstadoConciliacion
  nota: string
  meses: Array<{
    mes: number
    ingreso: number
    facturado: number
    diferencia: number
    facturas: number
  }>
}

/**
 * Como se lee cada estado. El TONO importa tanto como la etiqueta: pintar de
 * rojo un registro incompleto manda a investigar un descalce que no existe, y
 * lo unico que hace falta es cargar facturas.
 */
export const ESTADOS_CONCILIACION: Record<
  EstadoConciliacion,
  { label: string; tono: "ok" | "aviso" | "alerta" | "neutro"; ayuda: string }
> = {
  cuadra: {
    label: "Cuadra",
    tono: "ok",
    ayuda: "Lo facturado y el ingreso reconocido coinciden.",
  },
  diferencia: {
    label: "Descalce real",
    tono: "alerta",
    ayuda: "El registro esta completo y aun asi difieren. Vale la pena mirarlo: "
      + "es devengado contra facturado, no un dato que falte.",
  },
  registro_incompleto: {
    label: "Faltan facturas",
    tono: "aviso",
    ayuda: "No hay con que comparar todavia. La diferencia se explica porque "
      + "faltan facturas por cargar, no por un descalce del negocio.",
  },
  sin_ingreso: {
    label: "Sin ingreso",
    tono: "neutro",
    ayuda: "No hay ingreso reconocido en este pais y año.",
  },
}

/* ------------------------------------------------------------- facturas --- */

/** Donde cuenta una factura. Espejo del CHECK de la migracion `0005`. */
export type ConsiderarEn =
  | "ambos"
  | "solo_finanzas"
  | "solo_contabilidad"
  | "ninguno"

export const CONSIDERAR_EN: Array<{
  id: ConsiderarEn
  label: string
  ayuda: string
}> = [
  { id: "ambos", label: "En los dos",
    ayuda: "Cuenta en el modelo de gestion y en el libro contable." },
  { id: "solo_finanzas", label: "Solo finanzas",
    ayuda: "Cuenta en el modelo de gestion y no en el libro." },
  { id: "solo_contabilidad", label: "Solo contabilidad",
    ayuda: "Cuenta en el libro y no en el modelo de gestion." },
  { id: "ninguno", label: "En ninguno",
    ayuda: "No cuenta en ninguno de los dos lados." },
]

export function etiquetaConsiderar(id: string): string {
  return CONSIDERAR_EN.find((c) => c.id === id)?.label ?? id
}

/** `GET /finance/invoices` */
export interface FacturasRow {
  facturas: Factura[]
  hay_mas: boolean
  limite: number
  nota: string
}

export interface Factura {
  id: string
  pais: string
  numero: string
  documento_fiscal: string | null
  cliente: string
  nombre_comercial: string | null
  concepto: string | null
  fecha_emision: string | null
  fecha_pago: string | null
  /** Nunca vacio: el backend convierte NULL en "EMITIDA". */
  estado: string
  anio: number
  mes: number
  moneda: string | null
  valor_venta: number | null
  impuesto: number | null
  precio_venta: number | null
  detraccion: number | null
  retencion: number | null
  importe_usd: number
  considerar_en: ConsiderarEn
  motivo_exclusion: string | null
  origen: string
}

/**
 * Si una factura entra o no en la conciliacion de FINANZAS.
 *
 * Es la misma regla que aplica el backend al sumar (`considerar_en IN ('ambos',
 * 'solo_finanzas')` y estado distinto de ANULADA). Se repite aca por una razon
 * concreta: la fila tiene que poder DECIR por que no suma, y para eso la
 * pantalla necesita conocer la regla, no solo el total.
 */
export function cuentaEnFinanzas(f: Factura): boolean {
  if (f.estado === "ANULADA") return false
  return f.considerar_en === "ambos" || f.considerar_en === "solo_finanzas"
}

/** Por que una factura no suma. `null` si suma. */
export function porQueNoSuma(f: Factura): string | null {
  if (f.estado === "ANULADA") {
    return "Anulada: incluirla inventaria facturacion que no ocurrio."
  }
  if (cuentaEnFinanzas(f)) return null
  return f.motivo_exclusion ?? "Excluida a mano, sin motivo registrado."
}
